import React, { useRef } from 'react';
import QRCode from 'react-qr-code';
import { jsPDF } from 'jspdf';
import QRCodeGenerator from 'qrcode';
import ModalComponent from 'components/common/ModalComponent';
import ButtonComponent from 'components/common/ButtonComponent';
import 'styles/QRModal.css';
import { QR_TYPES } from 'constants/qr.types';
import PropTypes from 'prop-types';

const QRModal = ({ isOpen, onClose, worker }) => {
  const qrRef = useRef(null);

  const handlePrintQR = async () => {
    try {
      // Generate QR code as data URL
      const workerData = {
        qrType: QR_TYPES.WORKER,
        id: worker.workerId,
        name: worker.firstName,
        lastName: worker.lastName,
        phone: worker.phoneNumber,
        oldId: worker.oldId
      };

      const qrDataUrl = await QRCodeGenerator.toDataURL(JSON.stringify(workerData), {
        margin: 1,
        width: 256,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      // Create PDF with sticker dimensions
      const CM_TO_MM = 10;
      const PAGE_WIDTH = 4 * CM_TO_MM; // 40mm
      const PAGE_HEIGHT = 5 * CM_TO_MM; // 50mm
      const MARGIN = 2; // 2mm margin for more space

      // eslint-disable-next-line new-cap
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [PAGE_WIDTH, PAGE_HEIGHT]
      });

      // Header - Worker ID
      pdf.setFontSize(6);
      pdf.setTextColor(5, 81, 13); // #05510d
      const workerId = worker.oldId || worker.workerId;
      pdf.text(`ID: ${workerId}`, PAGE_WIDTH / 2, MARGIN + 2.5, { align: 'center' });

      // QR Code - slightly smaller to accommodate bigger fonts
      const qrSize = 24; // 24mm to make room for bigger text
      const qrX = (PAGE_WIDTH - qrSize) / 2;
      const qrY = MARGIN + 5;

      pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

      // Worker Name below QR - split into multiple lines if too long
      pdf.setFontSize(5);
      pdf.setTextColor(0, 0, 0);
      const firstName = worker.firstName;
      const lastName = worker.lastName;
      const maxLineLength = 18;

      // Check if we need to split the name
      if ((firstName + lastName).length > maxLineLength) {
        // Split into two lines: first name on first line, last name on second line
        pdf.text(firstName, PAGE_WIDTH / 2, qrY + qrSize + 3, { align: 'center' });
        pdf.text(lastName, PAGE_WIDTH / 2, qrY + qrSize + 5.5, { align: 'center' });
      } else {
        // Full name fits on one line
        const fullName = `${firstName} ${lastName}`;
        pdf.text(fullName, PAGE_WIDTH / 2, qrY + qrSize + 4, { align: 'center' });
      }

      // Footer - bigger font (adjust position based on name layout)
      pdf.setFontSize(4);
      pdf.setTextColor(5, 81, 13); // #05510d
      const footerY =
        (firstName + lastName).length > maxLineLength
          ? PAGE_HEIGHT - MARGIN + 1 // Lower position if name is on two lines
          : PAGE_HEIGHT - MARGIN;
      pdf.text('crops.io', PAGE_WIDTH / 2, footerY, { align: 'center' });

      // Download the PDF
      pdf.save(`worker-qr-${worker.workerId}.pdf`);
    } catch (err) {
      console.error('Error generating PDF:', err);
    }
  };

  if (!worker) return null;

  const workerData = {
    qrType: QR_TYPES.WORKER,
    id: worker.workerId,
    name: worker.firstName,
    lastName: worker.lastName,
    phone: worker.phoneNumber,
    oldId: worker.oldId
  };

  const customFooter = (
    <>
      <ButtonComponent
        onClick={handlePrintQR}
        className="bg-green-600 text-white hover:bg-green-700"
      >
        Print QR
      </ButtonComponent>
      <ButtonComponent
        onClick={onClose}
        customColorsStyles="bg-gray-100 text-greenPrimary hover:text-white"
      >
        Close
      </ButtonComponent>
    </>
  );

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title="Worker QR Code"
      customFooter={customFooter}
    >
      <div className="qr-content">
        <div className="worker-info mb-4">
          <h4 className="text-lg font-semibold">
            {worker.firstName} {worker.lastName}
          </h4>
          <p className="text-gray-600">ID: {worker.oldId || worker.workerId}</p>
        </div>

        <div className="qr-container mb-4" ref={qrRef}>
          <QRCode
            value={JSON.stringify(workerData)}
            size={256}
            style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
            viewBox="0 0 256 256"
          />
        </div>

        <div className="qr-description text-sm text-gray-500 text-center">
          Scan this QR code to access worker&apos;s information
        </div>
      </div>
    </ModalComponent>
  );
};

QRModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  worker: PropTypes.object
};

export default QRModal;
